import MaterialReactTable, {
  MRT_FullScreenToggleButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IExpensesForCashResponse } from '../../apis/models/transactions';
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { GroupedCashReportData } from './stats-report';
import AccountBreakdown from '../../components/AccountBreakdown';
import i18n from 'i18next';
import { IIncomesForCashResponse } from '../../apis/models/transactions';
import { downloadCombinedCashReport } from './downloadCashReport';

const ToggleGlobalFilter = MRT_ToggleGlobalFilterButton;
const ToggleFilters = MRT_ToggleFiltersButton;
const ShowHideColumns = MRT_ShowHideColumnsButton;
const FullScreenToggle = MRT_FullScreenToggleButton;

interface ICashReportDetailProps {
  expenseTransactionResult: IExpensesForCashResponse[];
  incomesTransactionResult: IIncomesForCashResponse[];
  groupedExpensesStats?: GroupedCashReportData[];
}

const ExpenseCashReportDetails: React.FC<ICashReportDetailProps> = ({
  expenseTransactionResult,
  incomesTransactionResult,
  groupedExpensesStats,
}) => {
  const { t } = useTranslation();
  const expensesFeeTypes = useSelector((state: RootState) => state.feeTypes.expenses);
  const incomeFeeTypes = useSelector((state: RootState) =>
    state.feeTypes.other.concat(state.feeTypes.student)
  );
  const [groupedExpenses, setGroupedExpenses] = useState<GroupedCashReportData[] | undefined>([]);
  const currentLanguage = i18n.language;
  const isFr = currentLanguage.includes('fr');

  const resolveFeeType = (enumKey: string): string => {
    const found = [...incomeFeeTypes, ...expensesFeeTypes].find((ft) => ft.enum_key === enumKey);
    if (found) return isFr ? found.value_fr : found.value_en;
    return enumKey;
  };
  const transactionsResult = useMemo(() => {
    return expenseTransactionResult.map((row) => {
      const translatedValue = expensesFeeTypes.find((item) => item.enum_key === row.feeCode);
      return { ...row, feeCode: translatedValue?.value_fr || 'Compte non trouvé' };
    });
  }, [expensesFeeTypes, expenseTransactionResult]);

  useEffect(() => {
    if (groupedExpensesStats && groupedExpensesStats.length > 0) {
      const groupedExpenseData = groupedExpensesStats.map((item) => {
        const translatedValue = expensesFeeTypes.find((feeType) => {
          return feeType && feeType.enum_key && feeType.enum_key === item.key;
        });
        return {
          ...item,
          translatedKey:
            currentLanguage.indexOf('fr') >= 0
              ? translatedValue?.value_fr || 'Compte non trouvé'
              : translatedValue?.value_en || 'Account not found',
        };
      });
      setGroupedExpenses(groupedExpenseData);
    }
  }, [groupedExpensesStats, expensesFeeTypes, currentLanguage]);

  const downloadReport = (table: any) => {
    const filteredExpenses = table.getFilteredRowModel().rows.map((row: any) => row.original);
    // Expenses tab downloads expenses only.
    downloadCombinedCashReport(
      [],
      filteredExpenses,
      resolveFeeType,
      {
        incomesTitle: t('reports_breakdown.incomes'),
        expensesTitle: t('reports_breakdown.expenses'),
        date: t('tables.date'),
        names: t('tables.names'),
        description: t('tables.description'),
        cashier: t('tables.employee_name'),
        amount: t('tables.amount'),
        beneficiary: t('tables.beneficiary_name'),
        authoriser: t('tables.authoriser_name'),
        subtotal: t('tables.subtotal'),
      },
      'cash-report.pdf'
    );
  };
  const columns = useMemo(
    () => [
      {
        header: t('tables.expense_code'),
        accessorKey: 'feeCode',
        size: 250,
      },
      {
        header: t('tables.date'),
        accessorKey: 'groupId',
      },
      {
        header: t('tables.beneficiary_name'),
        accessorKey: 'beneficiary_name',
      },
      {
        header: t('tables.employee_name'),
        accessorKey: 'cashier',
      },
      {
        header: t('tables.amount'),
        accessorKey: 'fee_amount',
        size: 100,
        aggregationFn: 'sum',
        AggregatedCell: ({ cell, table }) => {
          const colorValue = cell.getValue() < 0 ? 'success.main' : 'error.main';
          return (
            <>
              {t('tables.sum_by') +
                ' ' +
                table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}
              :{' '}
              <Box sx={{ color: colorValue, fontWeight: 'bold' }}>
                {cell.getValue()?.toLocaleString?.(navigator.language || 'fr-FR', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Box>
            </>
          );
        },
      },
      {
        header: t('tables.authoriser_name'),
        accessorKey: 'authorizer',
      },
      {
        header: t('tables.description'),
        accessorKey: 'description',
        enableGrouping: false,
        size: 300,
      },
    ],
    [t]
  );
  const total = useMemo(() => {
    const expenseTotal = transactionsResult.reduce((a, b) => a + b.fee_amount, 0);
    return expenseTotal.toFixed(2);
  }, [transactionsResult]);
  return (
    <>
      <Typography mb={1} variant="h3">
        {t('expenses_lbl') + total + '$'}
      </Typography>
      <MaterialReactTable
        // @ts-ignore
        columns={columns}
        data={transactionsResult}
        renderToolbarInternalActions={({ table }) => (
          <>
            <ToggleGlobalFilter table={table} />
            <IconButton onClick={() => downloadReport(table)}>
              <DownloadIcon />
            </IconButton>
            <ToggleFilters table={table} />
            <ShowHideColumns table={table} />
            <FullScreenToggle table={table} />
          </>
        )}
        enableColumnResizing
        enableGrouping
        enableStickyHeader
        enableStickyFooter
        initialState={{
          density: 'compact',
          expanded: true, //expand all groups by default
          pagination: { pageIndex: 0, pageSize: 15 },
        }}
        muiToolbarAlertBannerChipProps={{ color: 'primary' }}
        muiTableContainerProps={{ sx: { maxHeight: 700 } }}
      />
      <Box mt={1}>
        {groupedExpenses && groupedExpenses.length > 0 && (
          <AccountBreakdown data={groupedExpenses} />
        )}
      </Box>
    </>
  );
};
export default ExpenseCashReportDetails;
